import type { FormEvent } from 'react';
import { useState } from 'react';
import type { DominantEmotion, PlanAdherence } from 'shared';

import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import type { ReviewContextDto } from '../../lib/api/endpoints';
import { useSubmitReview } from '../reviews/useSubmitReview';

import { EngineSummary } from './EngineSummary';
import { ReviewHeader } from './ReviewHeader';
import { EmotionSelect, LessonInput, PlanAdherenceSelect } from './ReviewInputs';

export function DeepReviewCard({
  context,
  tradeId,
}: {
  context: ReviewContextDto;
  tradeId: string;
}) {
  const submitReview = useSubmitReview(context.lifecycle.accountId);
  const [planAdherence, setPlanAdherence] = useState<PlanAdherence | ''>('');
  const [dominantEmotion, setDominantEmotion] = useState<DominantEmotion | ''>('');
  const [oneLineLesson, setOneLineLesson] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [whatToChange, setWhatToChange] = useState('');
  const valid =
    Boolean(planAdherence) &&
    Boolean(dominantEmotion) &&
    whatHappened.trim().length >= 20 &&
    whatToChange.trim().length >= 20;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!planAdherence || !dominantEmotion || !valid) return;
    submitReview.mutate({
      tradeId,
      body: {
        mode: 'deep',
        planAdherence,
        dominantEmotion,
        ...(oneLineLesson.trim() ? { oneLineLesson: oneLineLesson.trim() } : {}),
        whatHappened: whatHappened.trim(),
        whatToChange: whatToChange.trim(),
      },
    });
  }

  return (
    <Card>
      <form className="space-y-5" onSubmit={submit}>
        <ReviewHeader context={context} />
        <PlanAdherenceSelect value={planAdherence} onChange={setPlanAdherence} />
        <EmotionSelect value={dominantEmotion} onChange={setDominantEmotion} />
        <LessonInput value={oneLineLesson} onChange={setOneLineLesson} />
        <EngineSummary summary={context.rulesSummary} />
        {context.flagsFired.length > 0 ? (
          <section className="space-y-2 rounded border border-warn/40 bg-warn/10 p-4">
            <h2 className="text-sm font-semibold">Behavioural flags fired</h2>
            {context.flagsFired.map((flag) => (
              <p key={`${flag.code}-${flag.description}`} className="text-sm text-text">
                <span className="font-medium">{flag.code}</span> - {flag.description}
              </p>
            ))}
          </section>
        ) : null}
        <ReviewTextarea
          id="what-happened"
          label="What happened?"
          maxLength={500}
          placeholder="Describe what drove the entry, the management, and the exit."
          value={whatHappened}
          onChange={setWhatHappened}
        />
        <ReviewTextarea
          id="what-to-change"
          label="What would you change?"
          maxLength={500}
          placeholder="Specific, actionable. Not 'be more disciplined' - name the rule or behaviour."
          value={whatToChange}
          onChange={setWhatToChange}
        />
        {submitReview.isError ? (
          <p className="text-sm text-danger">Review could not be submitted.</p>
        ) : null}
        {submitReview.isSuccess ? (
          <p className="text-sm text-success">Review submitted. Trade-plan gate cleared.</p>
        ) : null}
        <Button
          type="submit"
          className="min-h-11 w-full sm:w-auto"
          disabled={!valid || submitReview.isPending}
        >
          Submit review
        </Button>
      </form>
    </Card>
  );
}

function ReviewTextarea({
  id,
  label,
  maxLength,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  maxLength: number;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-medium" htmlFor={id}>
      {label}
      <textarea
        id={id}
        className="mt-1 min-h-28 w-full rounded border border-border bg-bg px-3 py-2 text-sm outline-none placeholder:text-text-subtle focus:border-primary focus:ring-2 focus:ring-primary/40"
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
